/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * Distances and point thinning for a course line.
 *
 * Points are `{ lat, lng }` in decimal degrees throughout — the same shape
 * parseGpx and geoLine hand back — so a drawn course and an uploaded one go
 * through exactly the same arithmetic.
 */

const EARTH_RADIUS_KM = 6371.0088;

const toRad = (deg) => (deg * Math.PI) / 180;

/** Great-circle distance between two points, in kilometres. */
export function haversineKm(a, b) {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Length of the whole line, in kilometres. 0 for fewer than two points. */
export function totalDistanceKm(points) {
  if (!Array.isArray(points) || points.length < 2) return 0;
  let km = 0;
  for (let i = 1; i < points.length; i += 1) km += haversineKm(points[i - 1], points[i]);
  return km;
}

/**
 * Does the line finish where it started? A loop course (a park lap, a 5K that
 * returns to the gate) closes within a few metres, never exactly.
 */
export function isLoop(points, toleranceKm = 0.03) {
  if (!Array.isArray(points) || points.length < 3) return false;
  return haversineKm(points[0], points[points.length - 1]) <= toleranceKm;
}

/**
 * Cheap first pass: drops every point within `minMetres` of the last one kept.
 * A 1-second GPS log is mostly points a couple of metres apart, and removing
 * them first is what keeps Douglas–Peucker fast on a 20,000-point upload.
 */
function radialThin(points, minMetres) {
  const out = [points[0]];
  let last = points[0];
  for (let i = 1; i < points.length - 1; i += 1) {
    if (haversineKm(last, points[i]) * 1000 >= minMetres) {
      out.push(points[i]);
      last = points[i];
    }
  }
  out.push(points[points.length - 1]);
  return out;
}

/**
 * Douglas–Peucker over `points`, iterative so a long course cannot overflow the
 * stack. Points are projected to metres once, up front, so the inner loop is
 * plain arithmetic — it runs O(n²) times on an awkward (zig-zag) track.
 */
function douglasPeucker(points, toleranceMetres) {
  const n = points.length;
  if (n <= 2) return points;
  const kx = Math.cos(toRad(points[0].lat)) * 111320;
  const ky = 110540;
  const xs = new Float64Array(n);
  const ys = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    xs[i] = (points[i].lng - points[0].lng) * kx;
    ys[i] = (points[i].lat - points[0].lat) * ky;
  }
  const tol2 = toleranceMetres * toleranceMetres;
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack = [0, n - 1];
  while (stack.length) {
    const last = stack.pop();
    const first = stack.pop();
    const ax = xs[first]; const ay = ys[first];
    const dx = xs[last] - ax; const dy = ys[last] - ay;
    const len2 = dx * dx + dy * dy;
    let max2 = 0;
    let index = -1;
    for (let i = first + 1; i < last; i += 1) {
      let px = xs[i] - ax; let py = ys[i] - ay;
      if (len2 > 0) {
        const t = Math.max(0, Math.min(1, (px * dx + py * dy) / len2));
        px -= t * dx; py -= t * dy;
      }
      const d2 = px * px + py * py;
      if (d2 > max2) { max2 = d2; index = i; }
    }
    if (index !== -1 && max2 > tol2) {
      keep[index] = 1;
      stack.push(first, index, index, last);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/** Last resort: every n-th point, ends kept. Linear, so it can never stall. */
function strideThin(points, maxPoints) {
  const step = (points.length - 1) / (maxPoints - 1);
  const out = [];
  for (let i = 0; i < maxPoints - 1; i += 1) out.push(points[Math.round(i * step)]);
  out.push(points[points.length - 1]);
  return out;
}

/**
 * Drops points that sit within `toleranceMetres` of the line their neighbours
 * already draw.
 *
 * A watch records a point every second, so a half marathon arrives as 7,000+
 * points that are almost all redundant. Stored whole they bloat the jsonb
 * column and the public page's payload, and they are impossible to edit by
 * hand. A 3 m tolerance keeps every corner a runner would notice.
 *
 * If the result is still above `maxPoints`, the tolerance is doubled a few
 * times — each pass on the previous, already smaller, result — and whatever is
 * still over the cap after that is thinned by stride. So a very long or very
 * noisy course still comes back editable, and quickly.
 */
export function simplifyPoints(input, { toleranceMetres = 3, maxPoints = 500 } = {}) {
  if (!Array.isArray(input) || input.length <= 2) return input || [];

  let tolerance = toleranceMetres;
  let result = douglasPeucker(radialThin(input, tolerance), tolerance);
  for (let pass = 0; result.length > maxPoints && pass < 4; pass += 1) {
    tolerance *= 2;
    result = douglasPeucker(result, tolerance);
  }
  return result.length > maxPoints ? strideThin(result, maxPoints) : result;
}

/**
 * Kilometres expressed in the unit the organizer picked, matched by the unit
 * row's label (the pack seeds Kilometre, Metre, Mile and Yard). Returns null for
 * a unit it does not recognise, so the caller leaves the distance alone rather
 * than writing a number in the wrong unit.
 */
export function kmToUnit(km, unitLabel) {
  const label = String(unitLabel || '').toLowerCase();
  if (/kilomet|^km\b/.test(label)) return km;
  if (/mile|^mi\b/.test(label)) return km * 0.621371;
  if (/yard|^yd\b/.test(label)) return km * 1093.6133;
  if (/metre|meter|^m\b|^mtr\b/.test(label)) return km * 1000;
  return null;
}

/** Rounds a distance for the form: two decimals, or whole numbers for m / yd. */
export function formatDistanceValue(value, unitLabel) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const whole = /metre|meter|yard|^m\b|^mtr\b|^yd\b/i.test(String(unitLabel || ''))
    && !/kilomet/i.test(String(unitLabel || ''));
  return whole ? String(Math.round(value)) : value.toFixed(2);
}
